import React, { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { AgentCard } from '../components/AgentCard';
import { VoiceInput } from '../components/VoiceInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowRight, GraduationCap, Building2, Briefcase, Users, Loader2, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const branchConfig = {
  formation: {
    icon: GraduationCap,
    color: 'bg-blue-500',
    lightColor: 'bg-blue-50',
    textColor: 'text-blue-600',
    image: 'https://images.unsplash.com/photo-1758691736067-b309ee3ef7b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0cmFpbmluZyUyMHdvcmtzaG9wJTIwZGl2ZXJzZSUyMGdyb3VwJTIwbW9kZXJufGVufDB8fHx8MTc2ODI1MzgxNXww&ixlib=rb-4.1.0&q=85',
  },
  pme: {
    icon: Building2,
    color: 'bg-emerald-500',
    lightColor: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    image: 'https://images.unsplash.com/photo-1565688527174-775059ac429c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxzbWFsbCUyMGJ1c2luZXNzJTIwdGVhbSUyMG1lZXRpbmclMjBtb2Rlcm4lMjBvZmZpY2UlMjBjb2xsYWJvcmF0aW9ufGVufDB8fHx8MTc2ODI1MzgxN3ww&ixlib=rb-4.1.0&q=85',
  },
  freelance: {
    icon: Briefcase,
    color: 'bg-purple-500',
    lightColor: 'bg-purple-50',
    textColor: 'text-purple-600',
    image: 'https://images.unsplash.com/photo-1696087225391-eb97abf5ba20?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwxfHxjcmVhdGl2ZSUyMGZyZWVsYW5jZXIlMjB3b3JraW5nJTIwaG9tZSUyMG9mZmljZSUyMGFlc3RoZXRpY3xlbnwwfHx8fDE3NjgyNTM4MTl8MA&ixlib=rb-4.1.0&q=85',
  },
  employes: {
    icon: Users,
    color: 'bg-amber-500',
    lightColor: 'bg-amber-50',
    textColor: 'text-amber-600',
    image: 'https://images.unsplash.com/photo-1748346918817-0b1b6b2f9bab?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxmb2N1c2VkJTIwb2ZmaWNlJTIwd29ya2VyJTIwZGF0YSUyMGFuYWx5c3QlMjBtb2Rlcm4lMjBkZXNrfGVufDB8fHx8MTc2ODI1MzgyMnww&ixlib=rb-4.1.0&q=85',
  }
};

const countryCodes = [
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+1', country: 'USA/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+32', country: 'Belgium', flag: '🇧🇪' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
  { code: '+212', country: 'Morocco', flag: '🇲🇦' },
  { code: '+221', country: 'Senegal', flag: '🇸🇳' },
];

export const BranchPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const branch = location.pathname.replace('/', '') || 'formation';
  const { t } = useLanguage();
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    country_code: '+33',
    needs: '',
    voice_transcript: ''
  });

  const config = branchConfig[branch] || branchConfig.formation;
  const Icon = config.icon;

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await axios.get(`${API}/agents`, { params: { branch } });
        setAgents(response.data);
      } catch (error) {
        console.error('Error fetching agents:', error);
      }
      setLoading(false);
    };
    fetchAgents();
  }, [branch]);

  const getBranchTitle = () => {
    switch (branch) {
      case 'formation': return t('branchFormation');
      case 'pme': return t('branchPME');
      case 'freelance': return t('branchFreelance');
      case 'employes': return t('branchEmployes');
      default: return '';
    }
  };

  const getBranchDesc = () => {
    switch (branch) {
      case 'formation': return t('branchFormationDesc');
      case 'pme': return t('branchPMEDesc');
      case 'freelance': return t('branchFreelanceDesc');
      case 'employes': return t('branchEmployesDesc');
      default: return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleVoiceTranscript = (transcript) => {
    setFormData(prev => ({
      ...prev,
      needs: prev.needs + (prev.needs ? ' ' : '') + transcript,
      voice_transcript: prev.voice_transcript + (prev.voice_transcript ? ' ' : '') + transcript
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.needs) {
      toast.error('Veuillez remplir tous les champs requis');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/clients`, {
        ...formData,
        branch
      });
      toast.success(t('success'));
      navigate(`/thank-you?clientId=${response.data.id}`);
    } catch (error) {
      console.error('Error:', error);
      toast.error(t('error'));
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen pt-20" data-testid={`branch-page-${branch}`}>
      {/* Hero Banner */}
      <section className="relative h-64 md:h-80 overflow-hidden">
        <img
          src={config.image}
          alt={getBranchTitle()}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/80 to-slate-900/40" />
        <div className="absolute inset-0 flex items-center">
          <div className="max-w-7xl mx-auto px-6 md:px-12 w-full">
            <div className="flex items-center gap-4 mb-4">
              <div className={`w-14 h-14 ${config.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                <Icon className="w-7 h-7 text-white" />
              </div>
              <h1 className="font-heading font-bold text-3xl md:text-4xl text-white">
                {getBranchTitle()}
              </h1>
            </div>
            <p className="text-slate-200 text-lg max-w-2xl">
              {getBranchDesc()}
            </p>
          </div>
        </div>
      </section>

      {/* Agents Grid */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between mb-8">
            <h2 className="font-heading font-semibold text-2xl text-slate-900">
              Nos Agents IA
            </h2>
            <Link to={`/register?branch=${branch}`}>
              <Button className="bg-primary hover:bg-primary-hover text-white rounded-full" data-testid="branch-cta-btn">
                {t('getStarted')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : agents.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-slate-500">Aucun agent disponible pour le moment</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {agents.map((agent) => (
                <AgentCard key={agent.id} agent={agent} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className={`py-16 ${config.lightColor}`}>
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-heading font-bold text-2xl md:text-3xl text-slate-900 mb-4">
            Besoin d'une solution personnalisée ?
          </h2>
          <p className="text-slate-600 mb-8">
            Contactez-nous pour discuter de vos besoins spécifiques
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to={`/register?branch=${branch}`}>
              <Button className="bg-primary hover:bg-primary-hover text-white rounded-full px-8 py-6" data-testid="branch-register-btn">
                Demander un devis
              </Button>
            </Link>
            <Link to="/appointment">
              <Button variant="outline" className="rounded-full px-8 py-6 border-2" data-testid="branch-appointment-btn">
                Prendre rendez-vous
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Section Inscription Directe */}
      <section className="py-20 bg-white" id="inscription" data-testid="branch-inscription-section">
        <div className="max-w-3xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10">
            <div className={`w-16 h-16 ${config.color} rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg`}>
              <Icon className="w-8 h-8 text-white" />
            </div>
            <h2 className="font-heading font-bold text-3xl text-slate-900 mb-4">
              Inscrivez-vous maintenant
            </h2>
            <p className="text-slate-600 max-w-xl mx-auto">
              Décrivez vos besoins en {getBranchTitle().toLowerCase()} et recevez un devis personnalisé
            </p>
          </div>

          <Card className="shadow-xl border-0" data-testid="branch-register-form-card">
            <CardHeader className={`${config.lightColor} border-b`}>
              <CardTitle className="text-lg flex items-center gap-2">
                <CheckCircle className={`w-5 h-5 ${config.textColor}`} />
                Formulaire d'inscription - {getBranchTitle()}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <form onSubmit={handleSubmit} className="space-y-5" data-testid="branch-register-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  {/* Name */}
                  <div className="space-y-2">
                    <Label htmlFor="name">{t('name')} *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Jean Dupont"
                      required
                      data-testid="branch-register-name"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label htmlFor="email">{t('email')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="jean@exemple.com"
                      required
                      data-testid="branch-register-email"
                    />
                  </div>
                </div>

                {/* Phone */}
                <div className="space-y-2">
                  <Label htmlFor="phone">{t('phone')}</Label>
                  <div className="flex gap-2">
                    <Select
                      value={formData.country_code}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, country_code: value }))}
                    >
                      <SelectTrigger className="w-28" data-testid="branch-register-country">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {countryCodes.map((country) => (
                          <SelectItem key={country.code} value={country.code}>
                            {country.flag} {country.code}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="06 12 34 56 78"
                      className="flex-1"
                      data-testid="branch-register-phone"
                    />
                  </div>
                </div>

                {/* Needs */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="needs">{t('needs')} *</Label>
                    <VoiceInput onTranscript={handleVoiceTranscript} />
                  </div>
                  <Textarea
                    id="needs"
                    name="needs"
                    value={formData.needs}
                    onChange={handleChange}
                    placeholder={`Décrivez vos besoins en ${getBranchTitle().toLowerCase()}...`}
                    rows={5}
                    required
                    data-testid="branch-register-needs"
                  />
                  {formData.voice_transcript && (
                    <p className="text-xs text-slate-500">
                      🎤 {t('voiceTranscript')}: {formData.voice_transcript}
                    </p>
                  )}
                </div>

                {/* Submit */}
                <Button
                  type="submit"
                  disabled={submitting}
                  className={`w-full ${config.color} hover:opacity-90 text-white rounded-full py-6 text-lg shadow-lg`}
                  data-testid="branch-register-submit"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Send className="w-5 h-5 mr-2" />
                      Envoyer ma demande
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
